import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"
import { setHidden } from "../lib/display_toggle"
import { setButtonLoading } from "../lib/button_loading"
import { confirmGoatProfilePhone } from "../lib/goat_profile_phone_confirm"

// Inline phone verification for onboarding wizard.
// Handles send code, verify code, resend — all via AJAX, no page reloads.
//
// Bad numbers (#13666): a send the carrier refuses outright comes back with
// `bad_number: true`; a send Twilio accepts but cannot deliver (dead handset,
// landline) is only reported later through the delivery callback, so after a
// code goes out this controller polls check_phone_code_status for that
// verdict. Either way it dispatches phone-verification-inline:delivery-failed
// so the rescue popup opens in its "update your number" state.
//
// Busy (DESIGN.md BUTTON-8): Send Code and Verify keep their label while a
// request is in flight — shared spinner, aria-busy, second click blocked
// (lib/button_loading). Only a finished send renames Send Code to "Resend".
export default class extends Controller {
  static targets = [
    "phoneInput", "phoneInputWrapper", "sendBtn",
    "codeSection", "codeInput", "verifyBtn",
    "phoneBadge", "message", "phoneFlow", "continueBtn"
  ]
  static values = {
    checkStatusUrl: String,
    pollIntervalMs: { type: Number, default: 3000 },
    pollMaxMs: { type: Number, default: 60000 }
  }

  disconnect() {
    this.stopDeliveryPoll()
  }

  async sendCode(event) {
    event.preventDefault()
    const phone = this.phoneInputTarget.value.trim()
    if (!phone) {
      this.showFieldFriction("Please enter a phone number.")
      return
    }

    this.stopDeliveryPoll()
    setButtonLoading(this.sendBtnTarget, true)
    let acknowledge = false
    let sent = false

    try {
      for (;;) {
        const response = await post("/onboarding/send_phone_code", {
          body: JSON.stringify({ phone, acknowledge_goat_number: acknowledge ? "1" : "0" }),
          contentType: "application/json",
          responseKind: 'json',
        })
        const data = await response.json

        if (data.error_code === "goat_number" && !acknowledge) {
          setButtonLoading(this.sendBtnTarget, false)
          const useAnyway = await confirmGoatProfilePhone()
          if (!useAnyway) return
          acknowledge = true
          setButtonLoading(this.sendBtnTarget, true)
          continue
        }

        if (data.success) {
          sent = true
          this.codeSectionTarget.classList.remove("d-none")
          this.codeInputTarget.focus()
          this.showMessage("Code sent! Check your phone.", "success")
          // Starts the OTP rescue timer (verification-rescue#codeSent, #11442).
          // Event name: phone-verification-inline:code-sent.
          this.dispatch("code-sent")
          this.startDeliveryPoll()
        } else if (data.bad_number) {
          this.reportBadNumber(data.message)
        } else {
          this.showMessage(data.message || "Failed to send code.", "error")
        }
        return
      }
    } catch {
      this.showMessage("Something went wrong. Please try again.", "error")
    } finally {
      setButtonLoading(this.sendBtnTarget, false)
      if (sent) setButtonLabel(this.sendBtnTarget, "Resend")
    }
  }

  async verifyCode(event) {
    event.preventDefault()
    const code = this.codeInputTarget.value.trim()
    if (!code || code.length < 6) {
      this.showFieldFriction("Please enter the 6-digit code.")
      return
    }

    setButtonLoading(this.verifyBtnTarget, true)

    try {
      const response = await post("/onboarding/verify_phone_code", {
        body: JSON.stringify({ code }),
        contentType: "application/json",
        responseKind: 'json',
      })
      const data = await response.json

      if (data.success) {
        this.stopDeliveryPoll()
        this.markVerified()
        this.showMessage("Phone verified!", "success")
      } else {
        this.showMessage(data.message || "Invalid code. Please try again.", "error")
        setButtonLoading(this.verifyBtnTarget, false)
      }
    } catch {
      this.showMessage("Something went wrong. Please try again.", "error")
      setButtonLoading(this.verifyBtnTarget, false)
    }
  }

  async resendCode(event) {
    event.preventDefault()
    await this.sendCode(event)
  }

  // verification-rescue:update-number-requested — the agent chose "Update my
  // number" on the bad-number popup. Put them back on the phone field with the
  // code entry closed; the next Send Code starts a clean attempt.
  focusPhone() {
    this.stopDeliveryPoll()
    if (this.hasCodeSectionTarget) this.codeSectionTarget.classList.add("d-none")
    if (this.hasCodeInputTarget) this.codeInputTarget.value = ""
    if (this.hasSendBtnTarget) setButtonLabel(this.sendBtnTarget, "Send Code")
    if (this.hasPhoneInputTarget) {
      this.phoneInputTarget.focus()
      this.phoneInputTarget.select()
    }
  }

  // Poll the delivery verdict for the code just sent. Stops on the first
  // terminal answer, on verification, on the next send, or after pollMaxMs —
  // by then the generic rescue popup has taken over.
  startDeliveryPoll() {
    if (!this.hasCheckStatusUrlValue || !this.checkStatusUrlValue) return

    this.pollStartedAt = Date.now()
    this.pollTimer = setInterval(() => this.checkDelivery(), this.pollIntervalMsValue)
  }

  stopDeliveryPoll() {
    if (!this.pollTimer) return

    clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  async checkDelivery() {
    if (Date.now() - this.pollStartedAt >= this.pollMaxMsValue) {
      this.stopDeliveryPoll()
      return
    }

    try {
      const response = await get(this.checkStatusUrlValue, { responseKind: "json" })
      const data = await response.json
      if (!data.failed) return

      this.stopDeliveryPoll()
      if (data.bad_number) {
        this.reportBadNumber(data.message)
      } else {
        this.showMessage("We couldn't deliver the code to that number. Try resending, or use a different number.", "error")
      }
    } catch (err) {
      // A failed poll is not a failed send; keep polling until the window ends.
      console.error("[phone-verification-inline] delivery check failed", err)
    }
  }

  // The NUMBER is the problem. Show the verdict inline (so it survives closing
  // the popup) and open the rescue popup in its bad-number state. Event name:
  // phone-verification-inline:delivery-failed.
  reportBadNumber(message) {
    const text = message || "That number can't receive our text messages. Please update your phone number and try again."
    this.showMessage(text, "error")
    this.dispatch("delivery-failed", { detail: { message: text } })
  }

  markVerified() {
    // Update badge
    if (this.hasPhoneBadgeTarget) {
      this.phoneBadgeTarget.className = "gl-onboarding-badge gl-onboarding-badge--verified"
      this.phoneBadgeTarget.innerHTML = '<i class="ph-fill ph-check-circle"></i> Verified'
    }
    // Dispatch so the wrapping verification-gate controller can re-evaluate
    // whether to enable the Continue button. Event name: phone-verification-inline:verified.
    this.dispatch("verified")

    // Hide phone flow
    if (this.hasPhoneFlowTarget) {
      setHidden(this.phoneFlowTarget, true)
    }

    // Enable continue button
    if (this.hasContinueBtnTarget) {
      this.continueBtnTarget.disabled = false
    }
  }

  showMessage(text, type) {
    this.renderMessage(text, type)
    if (type === "error") this.announceError(text)
  }

  // Local blank-field guard: shows the same message, but escalates through the
  // THROTTLED path. Clicking Send Code / Verify with an empty field is ordinary
  // form friction — popping the support prompt on the first click would nag every
  // user before anything has actually failed.
  showFieldFriction(text) {
    this.renderMessage(text, "error")
    this.dispatch("client-error", { detail: { message: text } })
  }

  renderMessage(text, type) {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = text
    this.messageTarget.className = `gl-onboarding-message ${type}`
  }

  // Tell the wizard root a step failed so signup-support can offer a ticket,
  // carrying the exact text the user saw. Event name:
  // phone-verification-inline:error.
  announceError(message) {
    this.dispatch("error", { detail: { message } })
  }
}

// Rename a button without dropping its leading icon: the text nodes are the
// label, an <i class="ph …"> beside them stays.
function setButtonLabel(button, label) {
  Array.from(button.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .forEach((node) => node.remove())
  button.appendChild(document.createTextNode(label))
}
